import { FEATURE_PROPERTY_SCHEMA } from "../core/constants/featurePropertySchema";
import type { Page } from "./Page";

export class DocsPage implements Page {
  render(): HTMLElement {
    const page = document.createElement("main");
    page.className = "page docs";
    page.innerHTML = `
      <section class="card">
        <p class="eyebrow">Project documentation</p>
        <h1>POC GIS Documentation</h1>
        <p>POC GIS is a static browser-based GIS tool for viewing GeoJSON, creating popup chart data, drawing/editing map features, aligning image overlays, and exporting clean GeoJSON files.</p>
      </section>

      <section class="card">
        <h2>Main pages</h2>
        <table>
          <tbody>
            <tr><th>Viewer</th><td>Import a GeoJSON map, import or generate chart JSON, click map features, and show popup bar charts linked by feature name.</td></tr>
            <tr><th>Drawer</th><td>Create and edit points, polylines, polygon regions, rectangles, and freehand areas. Edit properties, use image overlays, and export a GeoJSON FeatureCollection.</td></tr>
            <tr><th>Tools</th><td>Create point GeoJSON, extract coordinates, inspect GeoJSON files, export feature names, and create chart templates for research work.</td></tr>
            <tr><th>Docs</th><td>Reference for map properties, chart schema, drawer behavior, and deployment.</td></tr>
          </tbody>
        </table>
      </section>

      <section class="card">
        <h2>Viewer: map and chart workflow</h2>
        <ol class="compact-list">
          <li>Import a GeoJSON map file.</li>
          <li>Import a chart JSON file, or create one in Generate Chart.</li>
          <li>Make sure each GeoJSON feature has a useful <code>properties.name</code>.</li>
          <li>Make sure each chart row has a matching <code>country_name</code>.</li>
          <li>Click a feature on the map to open the popup chart.</li>
        </ol>
        <p>The matching rule is simple: <code>feature.properties.name</code> must equal <code>charts[].country_name</code>. Matching ignores upper/lower case and extra spaces. Property order inside the GeoJSON file does not matter.</p>
      </section>

      <section class="card">
        <h2>Generate Chart explanation</h2>
        <table>
          <tbody>
            <tr><th>Legend title</th><td>Title shown above the map legend. Example: <code>Population groups</code>.</td></tr>
            <tr><th>Legends</th><td>Shared bar categories and colors. Every chart value name should match a legend name to receive the correct color.</td></tr>
            <tr><th>Feature chart</th><td>One block for one map feature. Use one block for <code>Misr</code>, another for <code>Barqa</code>, another for any other named feature.</td></tr>
            <tr><th>Map feature name</th><td>The value that must match the GeoJSON feature <code>properties.name</code>.</td></tr>
            <tr><th>Chart unit</th><td>Unit for that specific feature chart, such as <code>%</code>, <code>km</code>, or <code>people</code>.</td></tr>
            <tr><th>Chart caption</th><td>Title text shown in that feature's popup chart.</td></tr>
            <tr><th>Chart max value</th><td>Controls the scale of that feature's bars. Use different max values when different regions need different scales.</td></tr>
            <tr><th>Values</th><td>Each value becomes one bar in the popup chart. The value name should match a legend name.</td></tr>
          </tbody>
        </table>
      </section>

      <section class="card">
        <h2>Drawer page</h2>
        <table>
          <tbody>
            <tr><th>Drawing modes</th><td>Pan, Select/Edit, Point, Polyline Boundary / Region, Polygon, Rectangle, and Freehand Area.</td></tr>
            <tr><th>Select/Edit</th><td>Select a feature to edit geometry and properties. Lines and polygons show draggable vertices and midpoint handles where Terra Draw supports them. Standard styling properties use fixed names; custom research properties are checked for duplicates, invalid names, and reserved drawing-engine keys.</td></tr>
            <tr><th>Image overlay</th><td>Add an uploaded image over the map. You can move, scale, rotate, change opacity, or remove it.</td></tr>
            <tr><th>Polyline Boundary / Region</th><td>Click points along a boundary. If the final point connects back to the first point, the line is saved as a filled polygon region with transparent color. Press Enter to keep it as an open polyline.</td></tr>
            <tr><th>Advanced JSON preview</th><td>Read-only live preview of the selected feature properties that will be saved and exported.</td></tr>
            <tr><th>Undo/Redo</th><td>Uses application-level GeoJSON snapshots for import, draw finish, delete, clear, property-save actions, and debounced geometry edits.</td></tr>
            <tr><th>Productivity actions</th><td>Fit current features to the map view, copy GeoJSON to clipboard, and review live feature counts before export.</td></tr>
            <tr><th>Export</th><td>Downloads a clean GeoJSON FeatureCollection without drawing-engine metadata.</td></tr>
          </tbody>
        </table>
      </section>

      <section class="card">
        <h2>Status messages</h2>
        <p>Status messages explain imports, errors, warnings, mode changes, and export actions. They stay visible until you close them with the X button so warnings are not missed during map editing.</p>
      </section>

      <section class="card">
        <h2>Property reference</h2>
        <p class="muted">The property table is generated from the same schema used by the Drawer editor. Color fields require hex values such as <code>#555555</code>. Opacity values must be between <code>0</code> and <code>1</code>. Custom property names must not duplicate standard properties or use reserved names such as <code>mode</code>, <code>selected</code>, or <code>midPoint</code>.</p>
        ${this.renderPropertyTable("Polygon")}
        ${this.renderPropertyTable("LineString")}
        ${this.renderPropertyTable("Point")}
      </section>

      <section class="card">
        <h2>Chart JSON schema</h2>
        <pre><code>{
  "legend_title": "Population groups",
  "legends": [
    { "name": "Urban", "color": "#2563eb" },
    { "name": "Rural", "color": "#16a34a" }
  ],
  "unit": "",
  "chart_caption": "",
  "chart_max_value": "",
  "charts": [
    {
      "country_name": "Misr",
      "unit": "%",
      "chart_caption": "Misr population distribution",
      "chart_max_value": 100,
      "properties": [
        { "name": "Urban", "value": 65 },
        { "name": "Rural", "value": 35 }
      ]
    }
  ]
}</code></pre>
        <p>The top-level <code>unit</code>, <code>chart_caption</code>, and <code>chart_max_value</code> are kept for compatibility with existing files. New chart builder rows store those fields inside each chart entry.</p>
      </section>

      <section class="card">
        <h2>Research tools</h2>
        <table>
          <tbody>
            <tr><th>GeoJSON inspector</th><td>Summarizes feature count, geometry types, property keys, feature names, bounding box, and viewport behavior.</td></tr>
            <tr><th>Feature names CSV</th><td>Exports names used for chart matching so the research team can review or clean them in a spreadsheet.</td></tr>
            <tr><th>Chart template</th><td>Creates a starter chart JSON file from the feature names found in a GeoJSON document.</td></tr>
            <tr><th>Coordinate extractor</th><td>Converts pasted <code>lng,lat</code> lines into reusable coordinate pairs.</td></tr>
          </tbody>
        </table>
      </section>

      <section class="card">
        <h2>Project structure</h2>
        <pre><code>src/app              App shell and hash router
src/config           Environment and map style configuration
src/core             Shared types, constants, schemas, and utilities
src/features/map     MapLibre map, GeoJSON layers, image overlays
src/features/drawing Terra Draw integration and draw-mode services
src/features/geojson GeoJSON parsing, loading, bounds, exporting
src/features/charts  Chart model, builder, popup rendering
src/features/coordinates Coordinate helpers
src/pages            Viewer, drawer, tools, docs pages
src/styles           Application CSS
docs                 Usage documentation</code></pre>
      </section>

      <section class="card">
        <h2>Deployment</h2>
        <ol class="compact-list">
          <li>Run <code>npm install</code>.</li>
          <li>Run <code>npm run build</code>.</li>
          <li>Push to GitHub.</li>
          <li>Set GitHub Pages source to GitHub Actions.</li>
          <li>The workflow deploys <code>dist/</code> to <code>/POC_GIS/</code>.</li>
        </ol>
      </section>
    `;
    return page;
  }

  private renderPropertyTable(type: keyof typeof FEATURE_PROPERTY_SCHEMA): string {
    const rows = FEATURE_PROPERTY_SCHEMA[type]
      .map((field) => `<tr><td><code>${field.key}</code>${field.aliases?.length ? `<br><span class="muted small-text">Aliases: ${field.aliases.map((alias) => `<code>${alias}</code>`).join(", ")}</span>` : ""}</td><td>${field.control}</td><td>${field.defaultValue === undefined ? "" : `<code>${this.escape(String(typeof field.defaultValue === "object" ? JSON.stringify(field.defaultValue) : field.defaultValue))}</code>`}</td><td>${this.escape(field.help)}</td></tr>`)
      .join("");
    return `<h3>${type} properties</h3><table><thead><tr><th>Property</th><th>Control</th><th>Default</th><th>Use</th></tr></thead><tbody>${rows}</tbody></table>`;
  }

  private escape(value: string): string {
    return value.replace(/[&<>]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[char] ?? char);
  }
}
